import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import * as persisted from '#/state/persisted'

type StateContext = boolean
type SetContext = (v: boolean) => void

const stateContext = createContext<StateContext>(
  Boolean(persisted.defaults['showVideos']),
)
stateContext.displayName = 'ShowVideosStateContext'
const setContext = createContext<SetContext>(() => {})
setContext.displayName = 'ShowVideosSetContext'

function getInitialShowVideos(): boolean {
  const persistedValue = persisted.get('showVideos')
  if (persistedValue === undefined) {
    return persisted.defaults.showVideos ?? true
  }
  return persistedValue
}

export function Provider({children}: React.PropsWithChildren<{}>) {
  const [state, setState] = useState<boolean>(getInitialShowVideos)

  const setStateWrapped = useCallback(
    (showVideos: boolean) => {
      setState(showVideos)
      void persisted.write('showVideos', showVideos)
    },
    [setState],
  )

  useEffect(() => {
    return persisted.onUpdate('showVideos', nextShowVideos => {
      setState(
        nextShowVideos === undefined
          ? (persisted.defaults.showVideos ?? true)
          : nextShowVideos,
      )
    })
  }, [setStateWrapped])

  return (
    <stateContext.Provider value={state}>
      <setContext.Provider value={setStateWrapped}>
        {children}
      </setContext.Provider>
    </stateContext.Provider>
  )
}

export function useShowVideos() {
  return useContext(stateContext)
}

export function useSetShowVideos() {
  return useContext(setContext)
}
